/**
 * TemplateModal — view / edit / create a document template, styled after
 * Noteble's DocumentModal. One component covers all three so the capture
 * Templates step can preview (eye icon), edit, and add templates with a single
 * dependency. Editing a platform template is copy-on-write server-side: the
 * save returns a new custom template, surfaced via onSaved.
 */

import { useEffect, useState } from 'react';
import { X, Pencil, Trash2, Check } from 'lucide-react';
import { Spinner, LoadingState } from '@systemdox/ui';
import { cn } from '@/lib/utils';
import { MarkdownRenderer } from '@/components/docs/MarkdownRenderer';
import { templatesApi, type TemplateMetadata, type TemplateDocType } from '@/services/templatesApi';
import { templateIcon, templateColor } from '@/capture/templateVisuals';

interface TemplateModalProps {
  mode: 'view' | 'create';
  /** Required for mode="view". */
  templateId?: string;
  /** Open a viewed template straight into edit mode (the row's edit action). */
  startEditing?: boolean;
  onClose: () => void;
  onSaved: (template: TemplateMetadata) => void;
  onDeleted?: (id: string) => void;
}

const DOC_TYPES: { key: TemplateDocType; label: string }[] = [
  { key: 'requirement', label: 'Requirement' },
  { key: 'design', label: 'Design' },
];

export function TemplateModal({ mode, templateId, startEditing, onClose, onSaved, onDeleted }: TemplateModalProps) {
  const [metadata, setMetadata] = useState<TemplateMetadata | null>(null);
  const [editing, setEditing] = useState(mode === 'create' || Boolean(startEditing));
  const [loading, setLoading] = useState(mode === 'view');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [docType, setDocType] = useState<TemplateDocType>('requirement');
  const [content, setContent] = useState('');

  useEffect(() => {
    if (mode !== 'view' || !templateId) return;
    let alive = true;
    setLoading(true);
    templatesApi.get(templateId)
      .then((detail) => {
        if (!alive) return;
        setMetadata(detail.metadata);
        setName(detail.metadata.name);
        setDescription(detail.metadata.description);
        setDocType(detail.metadata.docType);
        setContent(detail.content);
      })
      .catch((e) => { if (alive) setError(e instanceof Error ? e.message : 'Could not load template'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [mode, templateId]);

  const isPlatform = metadata?.type === 'platform';
  const Icon = templateIcon(metadata?.icon);
  const color = templateColor(metadata?.color);

  async function handleSave() {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const req = { name: name.trim(), description: description.trim(), docType, content };
      const saved = mode === 'create' || !metadata
        ? await templatesApi.create(req)
        : await templatesApi.update(metadata.id, req);
      onSaved(saved);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save template');
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!metadata || isPlatform) return;
    setDeleting(true);
    setError('');
    try {
      await templatesApi.remove(metadata.id);
      onDeleted?.(metadata.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete template');
      setDeleting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="fixed inset-0 bg-black/50" />
      <div
        className="relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-slate-200 p-4 dark:border-slate-800">
          <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', color.bg)}>
            <Icon className={cn('h-5 w-5', color.fg)} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold text-slate-900 dark:text-slate-100">
              {mode === 'create' ? 'New template' : metadata?.name || 'Template'}
            </h2>
            {metadata && (
              <p className="text-[11px] uppercase tracking-wide text-slate-400">
                {isPlatform ? 'Platform template' : 'Custom template'} · {metadata.docType}
              </p>
            )}
          </div>
          {metadata && !editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
              title="Edit template"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <LoadingState size="lg" />
          ) : editing ? (
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Runbook"
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Description</label>
                <input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="One line describing when to use it"
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Document type</label>
                <div className="grid grid-cols-2 gap-2">
                  {DOC_TYPES.map((d) => (
                    <button
                      key={d.key}
                      type="button"
                      onClick={() => setDocType(d.key)}
                      aria-pressed={docType === d.key}
                      className={cn(
                        'rounded-xl border py-2 text-sm font-medium transition-colors',
                        docType === d.key
                          ? 'border-blue-500 text-blue-700 ring-1 ring-blue-500 dark:text-blue-300'
                          : 'border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300',
                      )}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Body (markdown)</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={'# {name}\n\n## Overview\n...'}
                  spellCheck={false}
                  className="h-72 w-full resize-none rounded-xl border border-slate-300 bg-white px-3 py-2 font-mono text-xs leading-relaxed dark:border-slate-700 dark:bg-slate-950"
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  {'{name}'}, {'{date}'} and {'{author}'} are filled in when a document is created.
                </p>
              </div>
              {isPlatform && (
                <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                  Saving changes to a platform template creates your own editable copy.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {metadata?.description && (
                <p className="text-sm text-slate-500 dark:text-slate-400">{metadata.description}</p>
              )}
              <MarkdownRenderer content={content} />
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && (
          <div className="flex items-center gap-2 border-t border-slate-200 p-4 dark:border-slate-800">
            {editing ? (
              <>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  {saving ? <Spinner size="sm" tone="current" /> : <Check className="h-4 w-4" />}
                  {mode === 'create' ? 'Create template' : isPlatform ? 'Save as custom' : 'Save changes'}
                </button>
                <button
                  type="button"
                  onClick={() => (mode === 'create' || !metadata ? onClose() : setEditing(false))}
                  className="rounded-xl px-3 py-2 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl px-3 py-2 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              >
                Close
              </button>
            )}
            <div className="flex-1" />
            {metadata && !isPlatform && !editing && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm text-red-500 hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-900/20"
              >
                {deleting ? <Spinner size="sm" tone="current" /> : <Trash2 className="h-4 w-4" />}
                Delete
              </button>
            )}
          </div>
        )}
        {error && (
          <div className="border-t border-red-100 bg-red-50 px-4 py-2 text-xs text-red-600 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
